import React, { useState, useRef, useEffect, useMemo } from "react";
import { nanoid } from "nanoid";
import PropTypes from "prop-types";
import classNames from "classnames";
import styles from "./ObjectSelect.module.css";
import { ReactComponent as DownArrow } from "../../assets/Down_Arrow.svg";

/**
 * Interactive component. Custom select box.
 */
export function ObjectSelect({
  options,
  onChange,
  placeholder,
  disabled,
  selectedOption,
  name,
  className,
  textColor,
  formatDisplayOption,
  isError,
  isDateOption = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef();
  const [_options, setOptions] = useState([]);

  // NEW: added ability to store option id with option for meetings, decisions, etc
  useEffect(() => {
    var optionList = [];
    options.map((option) => {
      return optionList.push({ id: option?.id, option: option?.option });
    });
    setOptions(optionList);
  }, [options]);

  useEffect(() => {
    const checkClick = (e) => {
      if (isOpen && ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", checkClick);

    return () => {
      document.removeEventListener("mousedown", checkClick);
    };
  }, [isOpen]);
  const _selectedOptionValue =
    selectedOption && selectedOption.id
      ? selectedOption.option
      : selectedOption;

  // Compute the options that would actually be shown in the dropdown
  const visibleOptions = useMemo(() => {
    return _options.filter((_option) => {
      if (_option.id && selectedOption && selectedOption.id) {
        return _option.id !== selectedOption.id;
      }

      return (
        _option.option !==
        (_selectedOptionValue ? _selectedOptionValue : placeholder)
      );
    });
  }, [_options, selectedOption, _selectedOptionValue, placeholder]);

  // Only allow opening if there would be something to show
  const canOpen = !disabled && visibleOptions.length > 0;

  return (
    <div className={classNames(styles.container)} ref={ref}>
      <input
        className={classNames(styles["display-none"])}
        type="text"
        name={name}
        value={_selectedOptionValue}
        readOnly
      />
      <button
        disabled={disabled}
        onClick={() => {
          if (canOpen) setIsOpen(!isOpen);
        }}
        className={classNames(
          styles["dropdown-button"],
          styles.flex,
          styles.border,
          className,
          {
            [styles["open-placeholder"]]: _selectedOptionValue === "",
            [styles["error"]]: isError,
            [styles["disabled"]]: !canOpen,
          }
        )}
      >
        <span className={isDateOption ? styles.isDateOption : ""}>
          {_selectedOptionValue === ""
            ? placeholder
            : (formatDisplayOption &&
                formatDisplayOption(_selectedOptionValue)) ||
              _selectedOptionValue}
        </span>
        {canOpen && <DownArrow className={classNames(styles.arrow)} />}
      </button>
      <ul
        onClick={() => setIsOpen(false)}
        id={classNames(styles["custom-options"])}
        className={classNames(styles.border, {
          [styles.none]: !isOpen || visibleOptions.length === 0,
        })}
        style={{ color: textColor }}
      >
        {visibleOptions.map((option, index) => (
          <li
            key={nanoid()}
            onClick={() => {
              isOpen && onChange(option.id);
            }}
            className={classNames({
              [styles.topSeparator]: option.id === -1,
              [styles.bottomSeparator]: option.id === -3,
              [styles.isDateOption]: isDateOption,
            })}
          >
            {(formatDisplayOption && formatDisplayOption(option.option)) ||
              option.option}
          </li>
        ))}
      </ul>
    </div>
  );
}

ObjectSelect.propTypes = {
  /**
   * Array of options to map through.
   */
  options: PropTypes.array,
  /**
   * Function that fires when the user selects a new option.
   */
  onChange: PropTypes.func,
  /**
   * Placeholder for the input.
   */
  placeholder: PropTypes.string,
  /**
   * Boolean that determines if the select box is disabled.
   */
  disabled: PropTypes.bool,
  /**
   * Value of the currently selected option.
   */
  selectedOption: PropTypes.any,
  /**
   * Name attribute for the select input.
   */
  name: PropTypes.string,
  /**
   * Determines the text color for the options
   */
  textColor: PropTypes.string,
};

ObjectSelect.defaultProps = {
  /**
   * Defaults to avoid undefined.
   */
  options: [],
};
